import React from 'react'
import { Card, CardContent, Typography, Button, Box, Grid, Tooltip, IconButton } from '@mui/material'
import { Application } from '@otw/models'
import { CombinedUserData } from '@otw/models/user'
import InfoIcon from '@mui/icons-material/Info'
import { StatusChip, applicationStatusTone } from 'components/StatusChip'
import { MobileRecordCard } from 'components/MobileRecordCard'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { TOUCH_MIN } from 'core/theme.tokens'
import type { ApplicationStatusFilter } from 'components/ApplicationStatusTimeline'

const APPLICATION_STATUS_LABELS: Record<Application['status'], string> = {
  signing: 'Signing',
  ready: 'Ready',
  draft: 'Draft',
  pending: 'Pending',
  viewed: 'Viewed',
  corrections_needed: 'Corrections needed',
  approved: 'Approved',
  denied: 'Denied',
}

const formatApplicationAmountLine = (application: Application): string => {
  const unitCount = application.inventory_ids?.length ?? 0
  return `${unitCount.toLocaleString('en-US')} ${unitCount === 1 ? 'unit' : 'units'}`
}

const mobileActionButtonSx = {
  borderRadius: '12px',
  textTransform: 'none' as const,
  fontWeight: 600,
  minHeight: TOUCH_MIN,
  width: '100%',
}

interface ApplicationCardProps {
  application: Application
  statusFilter: ApplicationStatusFilter
  users: CombinedUserData[]
  onViewDocument: (application: Application) => void
  onAssignAgent: (application: Application) => void
  onMarkCompleted: (applicationId: string) => void
  onAddNotes: (application: Application) => void
  onViewDocuments: (application: Application) => void
  onViewApprovalNotes: (application: Application) => void
  onAssociateVehicles: (application: Application) => void
}

const ApplicationCard: React.FC<ApplicationCardProps> = ({
  application,
  statusFilter,
  users,
  onViewDocument,
  onAssignAgent,
  onMarkCompleted,
  onAddNotes,
  onViewDocuments,
  onViewApprovalNotes,
  onAssociateVehicles,
}) => {
  const isMobile = useIsMobile()
  const isSigningStatus = application.status === 'signing'
  const showAssignAgent = statusFilter === 'ready'
  const showMarkCompleted = statusFilter === 'viewed' || statusFilter === 'approved'
  const showBankNotes =
    statusFilter !== 'viewed' && statusFilter !== 'approved' && statusFilter !== 'denied'
  const showApprovalTerms =
    statusFilter === 'viewed' || statusFilter === 'approved' || statusFilter === 'denied'
  const showAssociateVehicles = statusFilter === 'denied'

  const userInfo = users?.find(user => user.email === application.user_email)
  const userName = userInfo
    ? `${userInfo.first_name} ${userInfo.last_name}`
    : application.user_name || 'Unknown User'

  const statusLabel = APPLICATION_STATUS_LABELS[application.status] ?? application.status

  const renderMobileActions = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, minWidth: 0 }}>
      {showAssignAgent && (
        <Button
          onClick={e => {
            e.stopPropagation()
            onAssignAgent(application)
          }}
          variant='contained'
          color='primary'
          size='small'
          sx={mobileActionButtonSx}
        >
          Assign Agent
        </Button>
      )}
      {showMarkCompleted && (
        <Button
          onClick={e => {
            e.stopPropagation()
            onMarkCompleted(application.id)
          }}
          variant='contained'
          color='primary'
          size='small'
          sx={mobileActionButtonSx}
        >
          Mark Completed
        </Button>
      )}
      {showBankNotes && (
        <Button
          onClick={e => {
            e.stopPropagation()
            onAddNotes(application)
          }}
          variant='contained'
          color='primary'
          size='small'
          sx={mobileActionButtonSx}
        >
          Bank Notes
        </Button>
      )}
      <Button
        onClick={e => {
          e.stopPropagation()
          onViewDocuments(application)
        }}
        variant='contained'
        color='primary'
        size='small'
        sx={mobileActionButtonSx}
      >
        Additional Documents
      </Button>
      {showApprovalTerms && (
        <Button
          onClick={e => {
            e.stopPropagation()
            onViewApprovalNotes(application)
          }}
          variant='contained'
          color='primary'
          size='small'
          sx={mobileActionButtonSx}
        >
          View Approval Terms
        </Button>
      )}
      {showAssociateVehicles && (
        <Button
          onClick={e => {
            e.stopPropagation()
            onAssociateVehicles(application)
          }}
          variant='contained'
          color='primary'
          size='small'
          sx={mobileActionButtonSx}
        >
          Associate Vehicles
        </Button>
      )}
    </Box>
  )

  if (isMobile) {
    return (
      <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
        <MobileRecordCard
          title={userName}
          meta={formatApplicationAmountLine(application)}
          detail={`Bank · ${application.bank_id}`}
          status={{ tone: applicationStatusTone(application.status), label: statusLabel }}
          onClick={isSigningStatus ? undefined : () => onViewDocument(application)}
        />
        {isSigningStatus ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, px: 0.5 }}>
            <InfoIcon sx={{ color: 'warning.main', fontSize: 20 }} />
            <Typography variant='caption' color='text.secondary'>
              Not viewable until the client submits the application.
            </Typography>
          </Box>
        ) : (
          renderMobileActions()
        )}
      </Box>
    )
  }

  return (
    <Card
      variant='outlined'
      sx={{
        bgcolor: 'background.paper',
        overflow: 'hidden',
        cursor: isSigningStatus ? 'default' : 'pointer',
        position: 'relative',
      }}
      onClick={() => !isSigningStatus && onViewDocument(application)}
    >
      <CardContent>
        <Box
          display='flex'
          justifyContent='space-between'
          sx={{
            mb: 3,
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant='overline' color='text.secondary'>
              Application ID
            </Typography>
            <Typography variant='h6' sx={{ fontWeight: 600 }}>
              {application.id}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant='overline' color='text.secondary'>
              Status
            </Typography>
            <StatusChip
              dense
              tone={applicationStatusTone(application.status)}
              label={statusLabel}
            />
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mb: isSigningStatus ? 5 : 3 }}>
          <Grid item xs={6}>
            <Typography variant='overline' color='text.secondary'>
              User
            </Typography>
            <Typography variant='body1'>{userName}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant='overline' color='text.secondary'>
              Bank Name
            </Typography>
            <Typography variant='body1'>{application.bank_id}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant='overline' color='text.secondary'>
              Date Submitted
            </Typography>
            <Typography variant='body1'>{application.time_created}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant='overline' color='text.secondary'>
              Bank Agent
            </Typography>
            <Typography variant='body1'>{application.bank_agent_id || 'No Agent Assigned'}</Typography>
          </Grid>
        </Grid>

        {isSigningStatus && (
          <Box sx={{ position: 'absolute', bottom: 16, right: 16 }}>
            <Tooltip 
              title="You cannot view applications until they have been initially submitted by the client" 
              placement="top"
            >
              <IconButton size="small">
                <InfoIcon sx={{ color: 'warning.main' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {!isSigningStatus && (
          <Box
            mt={2}
            display='flex'
            gap={1}
            justifyContent='center'
            flexWrap='wrap'
            sx={{
              '& .MuiButton-root': {
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                py: 1,
                minWidth: 120,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                },
              },
            }}
          >
            {showAssignAgent && (
              <Button
                onClick={e => {
                  e.stopPropagation()
                  onAssignAgent(application)
                }}
                variant='contained'
                color='primary'
                size='small'
                sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    color: '#fff',
                  },
                }}
              >
                Assign Agent
              </Button>
            )}

            {showMarkCompleted && (
              <Button
                onClick={e => {
                  e.stopPropagation()
                  onMarkCompleted(application.id)
                }}
                variant='contained'
                color='primary'
                size='small'
                sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    color: '#fff',
                  },
                }}
              >
                Mark Completed
              </Button>
            )}

            {showBankNotes && (
              <Button
                onClick={e => {
                  e.stopPropagation()
                  onAddNotes(application)
                }}
                variant='contained'
                color='primary'
                size='small'
                sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    color: '#fff',
                  },
                }}
              >
                Bank Notes
              </Button>
            )}

            <Button
              onClick={e => {
                e.stopPropagation()
                onViewDocuments(application)
              }}
              variant='contained'
              color='primary'
              size='small'
              sx={{
                borderRadius: '12px',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  color: '#fff',
                },
              }}
            >
              <Typography variant='body2'>Additional Documents</Typography>
            </Button>

            {showApprovalTerms && (
              <Button
                onClick={e => {
                  e.stopPropagation()
                  onViewApprovalNotes(application)
                }}
                variant='contained'
                color='primary'
                size='small'
                sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    color: '#fff',
                  },
                }}
              >
                View Approval Terms
              </Button>
            )}

            {showAssociateVehicles && (
              <Button
                onClick={e => {
                  e.stopPropagation()
                  onAssociateVehicles(application)
                }}
                variant='contained'
                color='primary'
                size='small'
                sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    color: '#fff',
                  },
                }}
              >
                Associate Vehicles
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default ApplicationCard
